<template>
  <!-- Level 2: the drilldown list (countries, sports) or the plain groups; the desktop featured groups are left out.
       Level 3 (`entryLabel` given): that entry's groups. -->
  <div class="flex flex-col gap-10">
    <section v-if="!entry && isDrilldown" class="flex flex-col gap-2">
      <h3 class="py-1 text-xs text-muted-foreground">{{ $t(entriesLabel) }}</h3>
      <ul class="flex flex-col gap-2">
        <li v-for="drilldownEntry in drilldownEntries" :key="drilldownEntry.label">
          <button
            type="button"
            class="flex w-full min-h-11 items-center justify-between gap-3 text-start text-base font-semibold"
            @click="emit('open', drilldownEntry.label)">
            <span class="flex items-center gap-3 min-w-0">
              <CountryFlag v-if="drilldownEntry.flag" :code="drilldownEntry.flag" small />
              <component :is="entryIcon(drilldownEntry)" v-else class="size-5 shrink-0" />
              <span class="truncate">{{ $t(drilldownEntry.label) }}</span>
            </span>
            <span class="flex size-11 shrink-0 items-center justify-center">
              <ChevronRightIcon class="size-7 text-muted-foreground" />
            </span>
          </button>
        </li>
      </ul>
    </section>

    <section v-for="group in groups" :key="group.label" class="flex flex-col gap-2">
      <h3 class="py-1 text-xs text-muted-foreground">{{ $t(group.label) }}</h3>
      <ul class="flex flex-col gap-2">
        <li v-for="link in group.links" :key="link.label">
          <RouterLink
            :to="link.to!"
            class="flex min-h-11 items-center text-base font-semibold"
            @click="emit('navigate')">
            <span class="truncate">{{ $t(link.label) }}</span>
          </RouterLink>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import CountryFlag from "@/components/CountryFlag.vue";
import { ChevronRightIcon } from "@xs2event/js-lib/icons";
import { entryIcon, toGroups, useMegaMenu } from "../mega/useMegaMenu";

const props = defineProps<{ item; entryLabel?: string }>();
const emit = defineEmits<{ open: [label: string]; navigate: [] }>();

const { visibleEntries, drilldownEntries, isDrilldown, entriesLabel, groupsLabel } = useMegaMenu(props.item);

// Looked up by label, so the level follows the entries when their teams resolve after it was opened (cold cache).
const entry = computed(() =>
  props.entryLabel
    ? drilldownEntries.value.find((drilldownEntry) => drilldownEntry.label === props.entryLabel)
    : undefined
);

const groups = computed(() => {
  if (entry.value) return toGroups(entry.value.items ?? [], "Events");
  return isDrilldown.value ? [] : toGroups(visibleEntries.value, groupsLabel.value);
});
</script>
